import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class OrsoHeaderCommerce extends Component {
  requiredRefs = ['cartLink', 'cartCount', 'cartStatus'];

  #listeners;
  #request;
  #requestTimer;
  #revision = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#stop();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    super.disconnectedCallback();
  }

  #start() {
    this.#listeners = new AbortController();
    const signal = this.#listeners.signal;
    document.addEventListener(StandardEvents.cartLinesUpdate, event => this.#cartUpdated(event), { signal });
    window.addEventListener('pageshow', event => {
      if (event.persisted) this.#refreshCount();
    }, { signal });
    this.#renderCount(Number(this.refs.cartCount.dataset.cartCount), false);
    if (window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward') this.#refreshCount();
  }

  #stop() {
    this.#listeners?.abort();
    this.#listeners = undefined;
    this.#revision += 1;
    window.clearTimeout(this.#requestTimer);
    this.#request?.abort();
    this.#request = undefined;
  }

  #renderCount(count, announce) {
    if (!Number.isSafeInteger(count) || count < 0) return;
    this.refs.cartCount.textContent = String(count);
    this.refs.cartCount.dataset.cartCount = String(count);
    this.refs.cartLink.setAttribute('aria-label', `${this.dataset.labelCart}: ${count}`);
    if (announce) this.refs.cartStatus.textContent = `${this.dataset.labelCount}: ${count}`;
  }

  async #cartUpdated(event) {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    const revision = ++this.#revision;
    this.#request?.abort();
    window.clearTimeout(this.#requestTimer);
    try {
      const result = await event.promise;
      if (!this.isConnected || revision !== this.#revision) return;
      const count = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
      this.#renderCount(count, true);
    } catch (_) {
      // Keep the last confirmed count; the next cart navigation renders it in Liquid.
    }
  }

  async #refreshCount() {
    this.#request?.abort();
    window.clearTimeout(this.#requestTimer);
    const controller = new AbortController();
    this.#request = controller;
    const revision = ++this.#revision;
    try {
      const url = new URL(`${this.dataset.cartUrl.replace(/\/$/, '')}.js`, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.endsWith('/cart.js') || url.search || url.hash) return;
      this.#requestTimer = window.setTimeout(() => controller.abort(), 8000);
      const response = await window.fetch(url.href, {
        method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
      });
      if (!response.ok) return;
      if (response.url) {
        const finalURL = new URL(response.url, window.location.origin);
        if (finalURL.origin !== url.origin || finalURL.pathname !== url.pathname) return;
      }
      const cart = await response.json();
      if (!this.isConnected || controller.signal.aborted || revision !== this.#revision) return;
      this.#renderCount(cart?.item_count, false);
    } catch (_) {
      // A failed count-only read never changes cart contents or the checkout path.
    } finally {
      if (this.#request === controller) {
        window.clearTimeout(this.#requestTimer);
        this.#request = undefined;
      }
    }
  }
}

if (!customElements.get('orso-header-commerce')) customElements.define('orso-header-commerce', OrsoHeaderCommerce);
